import type { Task } from '../../../types';
import type { TaskCardState } from './TaskCard';

export interface TaskStateEntry {
  taskId: string;
  state: TaskCardState;
  redoReason?: string;
  redoParentSubmissionId?: string;
}

export interface DeriveTaskStatesInput {
  tasks: Task[];
  /** Tasks with a submission doc in Firestore. */
  submittedTaskIds: Set<string>;
  /** Tasks with a resumable draft on this device. */
  draftTaskIds: Set<string>;
  /** Tasks with an upload queued / compressing / uploading / finalizing. */
  uploadingTaskIds: Set<string>;
  /** Tasks whose upload exhausted every retry. */
  failedTaskIds: Set<string>;
  /** The task submitted this session (celebration window). */
  recentlySubmittedTaskId: string | null;
  redoRequestsByTask: Map<string, { submissionId: string; reason: string }>;
}

/**
 * Pure derivation of every task card's state. Kept out of the page component
 * so the precedence rules are unit-testable.
 *
 * Precedence, highest first:
 *   1. submitted + redo requested       → needs_redo (unless a redo is in flight)
 *   2. upload in flight                 → pending    (survives a reload — the
 *                                          Firestore doc is only written when
 *                                          the upload finishes, so without this
 *                                          a recovered upload looked "not started"
 *                                          and invited a second recording)
 *   3. submitted                        → complete / just-completed
 *   4. just submitted, doc not yet seen → just-completed (unless failed)
 *   5. draft on disk                    → resume (never while its upload runs)
 */
export function deriveTaskStates(input: DeriveTaskStatesInput): TaskStateEntry[] {
  const {
    tasks, submittedTaskIds, draftTaskIds, uploadingTaskIds, failedTaskIds,
    recentlySubmittedTaskId, redoRequestsByTask,
  } = input;
  const states: TaskStateEntry[] = [];

  for (const t of tasks) {
    if (!submittedTaskIds.has(t.id)) continue;
    const recent = t.id === recentlySubmittedTaskId;
    const uploading = uploadingTaskIds.has(t.id);
    const redo = redoRequestsByTask.get(t.id);
    if (redo && !recent && !uploading) {
      states.push({
        taskId: t.id,
        state: 'needs_redo',
        redoReason: redo.reason,
        redoParentSubmissionId: redo.submissionId,
      });
    } else if (uploading && !recent) {
      // A redo/addendum recovered after a reload — show it uploading rather
      // than re-offering the redo.
      states.push({ taskId: t.id, state: 'pending' });
    } else {
      states.push({ taskId: t.id, state: recent ? 'just-completed' : 'complete' });
    }
  }

  // Firestore snapshot may not have arrived yet — the just-submitted task
  // gets 'just-completed' immediately so the card sequence runs.
  if (recentlySubmittedTaskId && !submittedTaskIds.has(recentlySubmittedTaskId)
      && !failedTaskIds.has(recentlySubmittedTaskId)) {
    states.push({ taskId: recentlySubmittedTaskId, state: 'just-completed' });
  }

  for (const taskId of uploadingTaskIds) {
    if (!submittedTaskIds.has(taskId) && taskId !== recentlySubmittedTaskId) {
      states.push({ taskId, state: 'pending' });
    }
  }

  for (const taskId of draftTaskIds) {
    if (!submittedTaskIds.has(taskId) && taskId !== recentlySubmittedTaskId
        && !uploadingTaskIds.has(taskId)) {
      states.push({ taskId, state: 'resume' });
    }
  }

  return states;
}
